import {
  GridColDef,
  GridFilterOperator,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { IntlShape } from 'react-intl';
import {
  formatTranslation,
  availableFieldLabelKey,
  issueLastDecisionStatusLabelKey,
} from 'intl';
import { CommonTranslationKeys, FiltersTranslationKeys, IssueTranslationKeys, WorkflowTranslationKeys } from 'intl/keys';
import { Link as RouterDomLink } from 'react-router-dom';
import { DataGridColumnsFactory } from 'components/common/dataGrid/columnsFactory';
import { withNegatedFilterOperators } from 'components/common/dataGrid/filters/negatedOperators';
import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { ISO6391LanguageCode } from 'shared/types/locale';
import { MemoIssueStage } from 'components/general/IssueStage';
import { IssueStageSize } from 'components/general/IssueStage/types';
import { useWorkflow } from 'store/workflow';
import { MemoProcessIcon } from 'components/common/ProcessIcon';
import { Box, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { ELLIPSIS_TEXT_SX } from 'components/common/dataGrid/styles';
import { GridColumnFieldIssue } from 'components/common/dataGrid/columnsFactory/types';
import { createDateWithTimeCellRenderer } from 'components/common/dataGrid/columnsFactory/cellRenderers';
import { makeIssueUserLabelValueGetter } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { ListCellWithModal } from 'components/common/dataGrid/columnsFactory/cellRenderers/listCellWithModal';
import {
  getIssueCreatedByOperator,
  getContractNumberOperator,
  getDateRangeOperator,
  getSiteOperator,
  getLevelOperator,
  getIssueStageOperator,
  getModifiedByOperator,
  getProcessOperator,
  getWorkflowOperator,
  getExecutorOperator,
  getUserOperator,
  getSupervisorOperator,
  getExecutedByCompanyOperator,
  getSubcontractorCompanyOperator,
  getWorkTypeOperator,
  getRootCauseOperator,
  getHazardCategoryOperator,
  getEnvironmentalAspectOperator,
  getCorrectiveActionTypeOperator,
  getDelayReasonOperator,
  getIssueVerificationOperator,
  getRejectionReasonOperator,
  getEffectOperator,
  getImpactOperator,
  getPriorityOperator,
  getProjectStageOperator,
  getDecisionToImposeFineOperator,
  getCircumstancesOperator,
  getSpilledSubstanceOperator,
  getEmploymentTypeOperator,
  getInjuryTypesOperator,
  getInjuredBodyPartsOperator,
  getRequirementsOperator,
  getInstallationBasisOperator,
  getConstructionProductDocumentsOperator,
  getDeviceDocumentsOperator,
  getAdditionalDocumentsOperator,
  getLastDecisionContainsOperator,
  getLastDecisionIsEmptyOperator,
  getDateIsEmptyOperator,
  getDateIsNotEmptyOperator,
  getIsEmptyOperator,
  getIsNotEmptyOperator,
} from 'components/common/dataGrid/columnsFactory/customFilters';
import { SORTABLE_ISSUE_FIELDS } from 'store/issue';
import { toJSDate } from 'shared/utils/date';
import { Color } from 'shared/utils/colors';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { NOT_APPLICABLE_FIELD_INDICATOR } from 'shared/contants';
import { calculateRemainingTimePercentage } from 'shared/domain/issue/calculateRemainingTimePercentage';
import { resolveTrafficLightLevelByPercentage } from 'shared/domain/issue/resolveTrafficLightLevel';
import {
  FILTERABLE_NUMBER_COLUMN_OPTIONS,
  FILTERABLE_TEXT_COLUMN_OPTIONS,
  GRID_STRING_FILTER_OPERATORS,
  ISSUE_DEADLINE_CLASS,
  ISSUE_DELAY_CLASS,
  ISSUE_NUMBER_FILTER_OPERATORS,
  ISSUE_PERCENTAGE_COLOR_BY_LEVEL,
  LAST_DECISION_STATUS_COLOR,
  NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS,
} from './constants';
import {
  IssueDeadlineFieldName,
  isPlannedStartDateStylingDisabled,
  isLastIssueStage,
  isTargetResponseDateStylingDisabled,
  isTargetCompletionDateStylingDisabled,
} from './utils';
import { APP_PATHS } from 'setup/navigation/paths';

function WorkflowNameCell({ workflowId }: { workflowId: string }) {
  const workflow = useWorkflow(workflowId);
  const name = workflow?.name ?? '';
  return (
    <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
      {name}
    </Typography>
  );
}

export class IssueColumnsFactory extends DataGridColumnsFactory<IssueOnSingleView> {
  constructor(
    intl: IntlShape,
    timezone: string,
    private organizationId: string,
    private processes: HashMap<{
      _id: string;
      color?: Color;
      code?: string;
    }>,
    private locale: ISO6391LanguageCode,
    private visibleFieldsMap: HashMap<HashMap<boolean>>,
    private readonly projectId: string
  ) {
    super(intl, timezone);
  }

  private readonly isFieldVisibleForRow = (
    row: IssueOnSingleView,
    fieldName: string
  ): boolean => {
    const processId = row?.process?._id;

    if (!processId) {
      return true;
    }

    const processVisibleFields = this.visibleFieldsMap[processId];

    if (!processVisibleFields) {
      return true;
    }

    return Boolean(processVisibleFields[fieldName]);
  };

  private readonly renderNotApplicableCell = () => (
    <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
      {NOT_APPLICABLE_FIELD_INDICATOR}
    </Typography>
  );

  private readonly renderProcessCell = (
    params: GridRenderCellParams<IssueOnSingleView>
  ) => {
    const processId = params.row.process?._id;
    const process = processId ? this.processes[processId] : undefined;

    if (!process) {
      const fallbackLabel = params.row.process?.label;

      if (!fallbackLabel) {
        return this.renderNotApplicableCell();
      }

      return (
        <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
          {fallbackLabel}
        </Typography>
      );
    }

    return (
      <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
        <MemoProcessIcon
          color={process.color ?? Color.black}
          code={process.code ?? ''}
        />
      </Stack>
    );
  };

  private readonly renderStageCell = (
    params: GridRenderCellParams<IssueOnSingleView>
  ) => (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ height: '100%', minWidth: 0 }}
    >
      <MemoIssueStage
        stageId={params.row.stageId}
        workflowId={params.row.workflowId}
        issueArchived={false}
        size={IssueStageSize.default}
      />
    </Stack>
  );

  private readonly renderWorkflowCell = (
    params: GridRenderCellParams<IssueOnSingleView>
  ) => <WorkflowNameCell workflowId={params.row.workflowId} />;

  private readonly renderTextCell = (
    params: GridRenderCellParams<IssueOnSingleView>
  ) => {
    const value = params.formattedValue ?? params.value;
    if (value === null || value === undefined) return <></>;
    return (
      <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
        {String(value)}
      </Typography>
    );
  };

  private readonly renderLinkCell = (
    to: string,
    label: string,
    color: Color = Color.primaryOrangeDark
  ) => (
    <Link
      component={RouterDomLink}
      to={to}
      underline='none'
      onClick={(event) => event.stopPropagation()}
      sx={{
        ...ELLIPSIS_TEXT_SX,
        cursor: 'pointer',
        typography: 'body2',
        display: 'inline-block',
        maxWidth: '100%',
        verticalAlign: 'bottom',
      }}
    >
      {/*
       * Color and hover underline both live on this inner span, not the
       * anchor: index.scss has a global `:any-link { color: inherit }`
       * reset that targets the <a> element itself, and text-decoration
       * color defaults to the element's own currentColor. Keeping either
       * one on the anchor would show a black underline under colored
       * (green/red) text.
       */}
      <Box
        component="span"
        sx={{
          color,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        {label}
      </Box>
    </Link>
  );

  private readonly renderInspectionNumberCell = (
    params: GridRenderCellParams<IssueOnSingleView>
  ) => {
    const inspectionId = params.row.inspection;
    const raw = params.formattedValue ?? params.value ?? '';
    const label = String(raw).trim();

    if (!inspectionId) {
      if (!label) {
        return this.renderNotApplicableCell();
      }

      return (
        <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
          {label}
        </Typography>
      );
    }

    if (!this.projectId) {
      return (
        <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
          {label || NOT_APPLICABLE_FIELD_INDICATOR}
        </Typography>
      );
    }

    const to = APP_PATHS.inspection.single(this.projectId, inspectionId);
    const text = label || NOT_APPLICABLE_FIELD_INDICATOR;

    return this.renderLinkCell(to, text, Color.black);
  };

  private readonly createEntityListCellRenderer = (label: string) => (
    params: GridRenderCellParams<IssueOnSingleView, LabelledEntity[] | undefined>
  ) => (
    <ListCellWithModal
      items={params.value ?? []}
      label={label}
      emptyPlaceholder=''
    />
  );

  private readonly formatIssueFieldHeader = (
    fieldName: IssueFieldNames
  ): string =>
    formatTranslation(this.intl, availableFieldLabelKey(fieldName));

  private readonly createExtendedDataLabelColumn = (
    fieldName: IssueFieldNames,
    filterOperators?: GridColDef<IssueOnSingleView>['filterOperators']
  ): GridColDef<IssueOnSingleView> => ({
    field: fieldName,
    headerName: this.formatIssueFieldHeader(fieldName),
    valueGetter: (_, row) => {
      const value =
        row?.extendedData?.[
        fieldName as keyof IssueOnSingleView['extendedData']
        ];

      if (!value || typeof value !== 'object' || !('label' in value)) {
        return null;
      }

      return (value as LabelledEntity).label;
    },
    renderCell: (params) =>
      this.isFieldVisibleForRow(params.row, fieldName)
        ? this.renderTextCell(params)
        : this.renderNotApplicableCell(),
    ...(filterOperators ? { filterOperators } : {}),
  });

  private readonly createExtendedDataValueColumn = (
    fieldName: IssueFieldNames,
    options?: Pick<
      GridColDef<IssueOnSingleView>,
      'type' | 'filterable' | 'filterOperators'
    >
  ): GridColDef<IssueOnSingleView> => ({
    field: fieldName,
    headerName: this.formatIssueFieldHeader(fieldName),
    type: options?.type,
    valueGetter: (_, row) =>
      row?.extendedData?.[fieldName as keyof IssueOnSingleView['extendedData']] ??
      null,
    renderCell: (params) =>
      this.isFieldVisibleForRow(params.row, fieldName)
        ? this.renderTextCell(params)
        : this.renderNotApplicableCell(),
    filterable: options?.filterable ?? false,
    ...(options?.filterOperators
      ? { filterOperators: options.filterOperators }
      : {}),
  });

  private readonly createExtendedDataNumberColumn = (
    fieldName: IssueFieldNames,
    options?: Pick<
      GridColDef<IssueOnSingleView>,
      'filterable' | 'filterOperators' | 'align' | 'headerAlign'
    >
  ): GridColDef<IssueOnSingleView> =>
    this.createExtendedDataValueColumn(fieldName, {
      type: 'number',
      ...options,
    });

  private readonly createExtendedDataDateColumn = (
    fieldName: IssueFieldNames,
    filterOperators: GridFilterOperator[]
  ): GridColDef<IssueOnSingleView> => {
    const dateRenderer = createDateWithTimeCellRenderer(
      this.locale,
      this.timezone
    );

    return {
      field: fieldName,
      headerName: this.formatIssueFieldHeader(fieldName),
      type: 'date',
      valueGetter: (_, row) => {
        const date =
          row?.extendedData?.[
          fieldName as keyof IssueOnSingleView['extendedData']
          ];
        return date ? toJSDate(date) : null;
      },
      renderCell: (params) =>
        this.isFieldVisibleForRow(params.row, fieldName)
          ? dateRenderer(params)
          : this.renderNotApplicableCell(),
      filterOperators,
    };
  };

  private readonly createEntityListColumn = (
    field: GridColumnFieldIssue,
    headerName: string,
    getValue: (row: IssueOnSingleView) => LabelledEntity[] | undefined,
    options?: Pick<GridColDef<IssueOnSingleView>, 'filterOperators' | 'filterable'>
  ): GridColDef<IssueOnSingleView> => ({
    field,
    headerName,
    valueGetter: (_, row) =>
      this.isFieldVisibleForRow(row, field) ? getValue(row) ?? [] : [],
    renderCell: (params) =>
      this.isFieldVisibleForRow(params.row, field)
        ? this.createEntityListCellRenderer(headerName)(params)
        : this.renderNotApplicableCell(),
    ...(options?.filterOperators ? { filterOperators: options.filterOperators } : {}),
    ...(options?.filterable !== undefined ? { filterable: options.filterable } : {}),
  });

  private issueNumber(): GridColDef<IssueOnSingleView> {
    return {
      field: GridColumnFieldIssue.issueNumber,
      headerName: formatTranslation(this.intl, IssueTranslationKeys.numberColumnHeader),
      valueGetter: (_, row) => row?.ncrNumber ?? '',
      renderCell: this.renderTextCell,
      filterOperators: ISSUE_NUMBER_FILTER_OPERATORS,
    };
  }

  private linkedInspectionNumber(): GridColDef<IssueOnSingleView> {
    return {
      field: GridColumnFieldIssue.inspectionNumber,
      headerName: formatTranslation(
        this.intl,
        IssueTranslationKeys.inspectionNumberColumnHeader
      ),
      valueGetter: (_, row) => row?.inspectionDisplayCode ?? '',
      renderCell: this.renderInspectionNumberCell,
      filterOperators: ISSUE_NUMBER_FILTER_OPERATORS,
    };
  }

  private title(): GridColDef<IssueOnSingleView> {
    return {
      field: GridColumnFieldIssue.title,
      headerName: formatTranslation(this.intl, IssueTranslationKeys.titleColumnHeader),
      valueGetter: (_, row) => row?.primaryData?.title ?? '',
      renderCell: this.renderTextCell,
    };
  }

  private stage(): GridColDef<IssueOnSingleView> {
    const issueStageOperator = getIssueStageOperator(this.intl);

    return {
      field: GridColumnFieldIssue.stage,
      headerName: formatTranslation(this.intl, FiltersTranslationKeys.filterTypeStage),
      valueGetter: (_, row) => row?.stageId,
      renderCell: this.renderStageCell,
      filterOperators: [issueStageOperator],
    };
  }

  private workflow(): GridColDef<IssueOnSingleView> {
    const workflowOperator = getWorkflowOperator(this.intl);

    return {
      field: GridColumnFieldIssue.workflow,
      headerName: formatTranslation(this.intl, WorkflowTranslationKeys.workflow),
      valueGetter: (_, row) => row?.workflowId,
      renderCell: this.renderWorkflowCell,
      sortable: false,
      filterOperators: [workflowOperator],
      minWidth: 160,
    };
  }

  private readonly renderLastDecisionCell = (
    params: GridRenderCellParams<IssueOnSingleView>
  ) => {
    const status = params.row.lastDecisionStatus;

    if (!status) {
      return this.renderNotApplicableCell();
    }

    const label = formatTranslation(
      this.intl,
      issueLastDecisionStatusLabelKey(status)
    );
    const color = LAST_DECISION_STATUS_COLOR[status];
    const latestDecisionId = params.row.latestDecisionId;

    if (!this.projectId || !latestDecisionId) {
      return (
        <Typography variant="body2" sx={{ ...ELLIPSIS_TEXT_SX, color }}>
          {label}
        </Typography>
      );
    }

    const to = APP_PATHS.issue.decision(
      this.projectId,
      params.row._id,
      latestDecisionId
    );

    return this.renderLinkCell(to, label, color ?? Color.primaryOrangeDark);
  };


  private lastDecision(): GridColDef<IssueOnSingleView> {
    const containsOperator = getLastDecisionContainsOperator(this.intl);
    const isEmptyOperator = getLastDecisionIsEmptyOperator(this.intl);

    return {
      field: GridColumnFieldIssue.lastDecision,
      headerName: formatTranslation(this.intl, IssueTranslationKeys.columnHeaderLastDecision),
      valueGetter: (_, row) => row?.lastDecisionStatus ?? null,
      renderCell: this.renderLastDecisionCell,
      filterOperators: [containsOperator, isEmptyOperator],
    };
  }

  private targetCompletionDate(): GridColDef<IssueOnSingleView> {
    const dateRangeOperator = getDateRangeOperator(this.intl, this.timezone);
    const dateIsEmpty = getDateIsEmptyOperator(this.intl);
    const dateIsNotEmpty = getDateIsNotEmptyOperator(this.intl);
    return {
      field: GridColumnFieldIssue.targetCompletionDate,
      headerName: this.formatIssueFieldHeader(
        IssueFieldNames.targetCompletionDate
      ),
      type: 'date',
      valueGetter: (_, row) => {
        const d = row?.extendedData?.targetCompletionDate;
        return d ? toJSDate(d) : null;
      },
      renderCell: createDateWithTimeCellRenderer(
        this.locale,
        this.timezone
      ),
      cellClassName: (params) =>
        this.resolveDeadlineCellClassName(
          params.row,
          IssueFieldNames.targetCompletionDate
        ),
      filterOperators: [dateRangeOperator, dateIsEmpty, dateIsNotEmpty],
    };
  }

  private targetResponseToAuthorDate(
    dateRangeOperator: ReturnType<typeof getDateRangeOperator>,
    dateIsEmptyOperator: GridFilterOperator,
    dateIsNotEmptyOperator: GridFilterOperator
  ): GridColDef<IssueOnSingleView> {
    const fieldName = IssueFieldNames.targetResponseToAuthorDate;
    const dateRenderer = createDateWithTimeCellRenderer(
      this.locale,
      this.timezone
    );

    return {
      field: fieldName,
      headerName: this.formatIssueFieldHeader(fieldName),
      type: 'date',
      valueGetter: (_, row) => {
        const date = row?.extendedData?.targetResponseToAuthorDate;
        return date ? toJSDate(date) : null;
      },
      renderCell: (params) =>
        this.isFieldVisibleForRow(params.row, fieldName)
          ? dateRenderer(params)
          : this.renderNotApplicableCell(),
      cellClassName: (params) =>
        this.resolveDeadlineCellClassName(params.row, fieldName),
      filterOperators: [dateRangeOperator, dateIsEmptyOperator, dateIsNotEmptyOperator],
    };
  }

  private amountPercentage(): GridColDef<IssueOnSingleView> {
    const fieldName = IssueFieldNames.amountPercentage;

    return {
      field: fieldName,
      headerName: this.formatIssueFieldHeader(fieldName),
      type: 'number',
      ...NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS,
      valueGetter: (_, row) => row?.extendedData?.amountPercentage ?? null,
      renderCell: (params) => {
        if (!this.isFieldVisibleForRow(params.row, fieldName)) {
          return this.renderNotApplicableCell();
        }

        const value =
          typeof params.value === 'number'
            ? params.value
            : Number(params.value);

        if (!Number.isFinite(value)) {
          return this.renderTextCell(params);
        }

        const normalizedValue = Math.max(0, Math.min(100, value));
        const valueLevel = resolveTrafficLightLevelByPercentage(value);
        const valueColor = ISSUE_PERCENTAGE_COLOR_BY_LEVEL[valueLevel];

        return (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={normalizedValue}
              sx={{
                flexGrow: 1,
                minWidth: 64,
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.300',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: valueColor,
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: valueColor, minWidth: 36, fontWeight: 700 }}
            >
              {`${Math.round(value)}%`}
            </Typography>
          </Stack>
        );
      },
      filterable: true,
      filterOperators: GRID_STRING_FILTER_OPERATORS,
    };
  }

  private resolveDelayCellClassName(
    row: IssueOnSingleView,
    delayFieldName: IssueFieldNames,
    finalDateFieldName: keyof NonNullable<IssueOnSingleView['extendedData']>
  ): string {
    if (!this.isFieldVisibleForRow(row, delayFieldName)) {
      return '';
    }

    // Don't style if issue is resolved
    if (isLastIssueStage(row?.stageId, row?.workflowId)) {
      return '';
    }

    // Don't style if action was taken (final date exists)
    if (row?.extendedData?.[finalDateFieldName]) {
      return '';
    }

    const delayValue = String(
      row?.extendedData?.[delayFieldName as keyof NonNullable<IssueOnSingleView['extendedData']>] ?? ''
    ).trim();

    const isNotApplicable =
      delayValue.toUpperCase() ===
      NOT_APPLICABLE_FIELD_INDICATOR.toUpperCase();
    const isZeroDelay = /^0+([.,]0+)?$/.test(delayValue);

    return delayValue && !isNotApplicable && !isZeroDelay
      ? ISSUE_DELAY_CLASS.red
      : '';
  }

  private startDateDelay(): GridColDef<IssueOnSingleView> {
    const fieldName = IssueFieldNames.startDateDelay;

    return {
      ...this.createExtendedDataValueColumn(fieldName, {
        ...FILTERABLE_TEXT_COLUMN_OPTIONS,
      }),
      cellClassName: (params) =>
        this.resolveDelayCellClassName(
          params.row,
          fieldName,
          'finalStartDate'
        ),
    };
  }

  private completionDateDelay(): GridColDef<IssueOnSingleView> {
    const fieldName = IssueFieldNames.completionDateDelay;

    return {
      ...this.createExtendedDataValueColumn(fieldName, {
        ...FILTERABLE_TEXT_COLUMN_OPTIONS,
      }),
      cellClassName: (params) =>
        this.resolveDelayCellClassName(
          params.row,
          fieldName,
          'finalCompletionDate'
        ),
    };
  }

  private responseToAuthorDelay(): GridColDef<IssueOnSingleView> {
    const fieldName = IssueFieldNames.responseToAuthorDelay;

    return {
      ...this.createExtendedDataValueColumn(fieldName, {
        ...FILTERABLE_TEXT_COLUMN_OPTIONS,
      }),
      cellClassName: (params) =>
        this.resolveDelayCellClassName(
          params.row,
          fieldName,
          'finalResponseToAuthorDate'
        ),
    };
  }

  private resolveDeadlineLevel(remainingPercentage: number): keyof typeof ISSUE_DEADLINE_CLASS {
    if (remainingPercentage >= 50) {
      return 'green';
    }

    if (remainingPercentage >= 10) {
      return 'yellow';
    }

    if (remainingPercentage >= 0) {
      return 'orange';
    }

    return 'red';
  }

  private isDeadlineStylingDisabled(
    row: IssueOnSingleView,
    fieldName: IssueDeadlineFieldName
  ): boolean {
    return (
      !this.isFieldVisibleForRow(row, fieldName) ||
      isLastIssueStage(row?.stageId, row?.workflowId) ||
      isPlannedStartDateStylingDisabled(row, fieldName) ||
      isTargetCompletionDateStylingDisabled(row, fieldName) ||
      isTargetResponseDateStylingDisabled(row, fieldName)
    );
  }

  private resolveDeadlineCellClassName(
    row: IssueOnSingleView,
    fieldName: IssueDeadlineFieldName
  ): string {
    if (this.isDeadlineStylingDisabled(row, fieldName)) {
      return '';
    }

    const startDate = row?.primaryData?.detectionDate ?? row?.createdAt;
    const remainingPercentage = calculateRemainingTimePercentage({
      startDate,
      deadlineDate: row?.extendedData?.[fieldName],
    });

    if (remainingPercentage == null) {
      return '';
    }

    // Don't style deadline cell when deadline is exceeded (remainingPercentage < 0)
    // In this case, only the delay cell should be styled
    if (remainingPercentage < 0) {
      return '';
    }

    return ISSUE_DEADLINE_CLASS[this.resolveDeadlineLevel(remainingPercentage)];
  }

  private finalCompletionDate(): GridColDef<IssueOnSingleView> {
    const dateRangeOperator = getDateRangeOperator(this.intl, this.timezone);
    const dateIsEmpty = getDateIsEmptyOperator(this.intl);
    const dateIsNotEmpty = getDateIsNotEmptyOperator(this.intl);
    return {
      field: GridColumnFieldIssue.finalCompletionDate,
      headerName: this.formatIssueFieldHeader(
        IssueFieldNames.finalCompletionDate
      ),
      type: 'date',
      valueGetter: (_, row) => {
        const d = row?.extendedData?.finalCompletionDate;
        return d ? toJSDate(d) : null;
      },
      renderCell: createDateWithTimeCellRenderer(
        this.locale,
        this.timezone
      ),
      filterOperators: [dateRangeOperator, dateIsEmpty, dateIsNotEmpty],
    };
  }

  private assignee(): GridColDef<IssueOnSingleView> {
    const assigneeOperator = getUserOperator(this.intl);
    const isEmptyOp = getIsEmptyOperator(this.intl);
    const isNotEmptyOp = getIsNotEmptyOperator(this.intl);
    return {
      field: GridColumnFieldIssue.assignee,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.assignee),
      valueGetter: (_, row) => row?.primaryData?.assignee?.label ?? null,
      renderCell: this.renderTextCell,
      filterOperators: [assigneeOperator, isEmptyOp, isNotEmptyOp],
    };
  }

  private description(): GridColDef<IssueOnSingleView> {
    return {
      field: GridColumnFieldIssue.description,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.description),
      valueGetter: (_, row) => row?.primaryData?.description ?? '',
      renderCell: this.renderTextCell,
      filterable: false,
    };
  }

  private detectionDate(): GridColDef<IssueOnSingleView> {
    const dateRangeOperator = getDateRangeOperator(this.intl, this.timezone);
    const dateIsEmpty = getDateIsEmptyOperator(this.intl);
    const dateIsNotEmpty = getDateIsNotEmptyOperator(this.intl);
    return {
      field: GridColumnFieldIssue.detectionDate,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.detectionDate),
      type: 'date',
      valueGetter: (_, row) => {
        const d = row?.primaryData?.detectionDate;
        return d ? toJSDate(d) : null;
      },
      renderCell: createDateWithTimeCellRenderer(
        this.locale,
        this.timezone
      ),
      filterOperators: [dateRangeOperator, dateIsEmpty, dateIsNotEmpty],
    };
  }

  private targetStartDate(): GridColDef<IssueOnSingleView> {
    const dateRangeOperator = getDateRangeOperator(this.intl, this.timezone);
    const dateIsEmpty = getDateIsEmptyOperator(this.intl);
    const dateIsNotEmpty = getDateIsNotEmptyOperator(this.intl);
    const fieldName = IssueFieldNames.targetStartDate;

    return {
      field: GridColumnFieldIssue.targetStartDate,
      headerName: this.formatIssueFieldHeader(fieldName),
      type: 'date',
      valueGetter: (_, row) => {
        const d = row?.extendedData?.targetStartDate;
        return d ? toJSDate(d) : null;
      },
      renderCell: createDateWithTimeCellRenderer(
        this.locale,
        this.timezone
      ),
      cellClassName: (params) =>
        this.resolveDeadlineCellClassName(params.row, fieldName),
      filterOperators: [dateRangeOperator, dateIsEmpty, dateIsNotEmpty],
    };
  }

  private finalStartDate(): GridColDef<IssueOnSingleView> {
    const dateRangeOperator = getDateRangeOperator(this.intl, this.timezone);
    const dateIsEmpty = getDateIsEmptyOperator(this.intl);
    const dateIsNotEmpty = getDateIsNotEmptyOperator(this.intl);
    return {
      field: GridColumnFieldIssue.finalStartDate,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.finalStartDate),
      type: 'date',
      valueGetter: (_, row) => {
        const d = row?.extendedData?.finalStartDate;
        return d ? toJSDate(d) : null;
      },
      renderCell: createDateWithTimeCellRenderer(
        this.locale,
        this.timezone
      ),
      filterOperators: [dateRangeOperator, dateIsEmpty, dateIsNotEmpty],
    };
  }

  private executor(): GridColDef<IssueOnSingleView> {
    const executorOperator = getExecutorOperator(this.intl);
    return {
      field: GridColumnFieldIssue.executor,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.executor),
      valueGetter: (_, row) => row?.primaryData?.executor?.label ?? null,
      renderCell: this.renderTextCell,
      filterOperators: [executorOperator, getIsEmptyOperator(this.intl), getIsNotEmptyOperator(this.intl)],
    };
  }

  private supervisor(): GridColDef<IssueOnSingleView> {
    const supervisorOperator = getSupervisorOperator(this.intl);
    return {
      field: GridColumnFieldIssue.supervisor,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.supervisor),
      valueGetter: (_, row) => row?.extendedData?.supervisor?.label ?? null,
      renderCell: this.renderTextCell,
      filterOperators: [supervisorOperator, getIsEmptyOperator(this.intl), getIsNotEmptyOperator(this.intl)],
    };
  }

  private issueCreatedAt(): GridColDef<IssueOnSingleView> {
    return {
      ...this.createdAt(this.intl, this.timezone),
      headerName: this.formatIssueFieldHeader(IssueFieldNames.creationDate),
    };
  }

  private issueCreatedBy(
    filterOperators: GridColDef<IssueOnSingleView>['filterOperators']
  ): GridColDef<IssueOnSingleView> {
    return {
      ...this.createdBy(this.organizationId, this.intl),
      field: GridColumnFieldIssue.createdBy,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.createdBy),
      valueGetter: makeIssueUserLabelValueGetter('createdBy', this.intl),
      filterOperators,
    };
  }

  private issueModifiedBy(
    filterOperators: GridColDef<IssueOnSingleView>['filterOperators']
  ): GridColDef<IssueOnSingleView> {
    return {
      ...this.modifiedBy(this.organizationId, this.intl),
      field: GridColumnFieldIssue.modifiedBy,
      headerName: this.formatIssueFieldHeader(IssueFieldNames.modifiedBy),
      valueGetter: makeIssueUserLabelValueGetter('modifiedBy', this.intl),
      filterOperators,
    };
  }

  create(): GridColDef<IssueOnSingleView>[] {
    const siteOperator = getSiteOperator(this.intl);
    const levelOperator = getLevelOperator(this.intl);
    const processOperator = getProcessOperator(this.intl);
    const issueCreatedByOperator = getIssueCreatedByOperator(this.intl);
    const modifiedByOperator = getModifiedByOperator(this.intl);
    const contractNumberOperator = getContractNumberOperator(this.intl);
    const subcontractorCompanyOperator = getSubcontractorCompanyOperator(this.intl);
    const executedByCompanyOperator = getExecutedByCompanyOperator(this.intl);
    const workTypeOperator = getWorkTypeOperator(this.intl);
    const rootCauseOperator = getRootCauseOperator(this.intl);
    const hazardCategoryOperator = getHazardCategoryOperator(this.intl);
    const environmentalAspectOperator = getEnvironmentalAspectOperator(this.intl);
    const correctiveActionTypeOperator = getCorrectiveActionTypeOperator(this.intl);
    const delayReasonOperator = getDelayReasonOperator(this.intl);
    const issueVerificationOperator = getIssueVerificationOperator(this.intl);
    const rejectionReasonOperator = getRejectionReasonOperator(this.intl);
    const effectOperator = getEffectOperator(this.intl);
    const impactOperator = getImpactOperator(this.intl);
    const priorityOperator = getPriorityOperator(this.intl);
    const projectStageOperator = getProjectStageOperator(this.intl);
    const decisionToImposeFineOperator = getDecisionToImposeFineOperator(this.intl);
    const circumstancesOperator = getCircumstancesOperator(this.intl);
    const spilledSubstanceOperator = getSpilledSubstanceOperator(this.intl);
    const employmentTypeOperator = getEmploymentTypeOperator(this.intl);
    const injuryTypesOperator = getInjuryTypesOperator(this.intl);
    const injuredBodyPartsOperator = getInjuredBodyPartsOperator(this.intl);
    const requirementsOperator = getRequirementsOperator(this.intl);
    const installationBasisOperator = getInstallationBasisOperator(this.intl);
    const constructionProductDocumentsOperator = getConstructionProductDocumentsOperator(this.intl);
    const deviceDocumentsOperator = getDeviceDocumentsOperator(this.intl);
    const additionalDocumentsOperator = getAdditionalDocumentsOperator(this.intl);
    const dateRangeOperator = getDateRangeOperator(this.intl, this.timezone);
    const isEmptyOperator = getIsEmptyOperator(this.intl);
    const isNotEmptyOperator = getIsNotEmptyOperator(this.intl);
    const dateIsEmptyOperator = getDateIsEmptyOperator(this.intl);
    const dateIsNotEmptyOperator = getDateIsNotEmptyOperator(this.intl);
    const subcontractorsLabel = this.formatIssueFieldHeader(
      IssueFieldNames.subcontractors
    );
    const contractNumbersLabel = this.formatIssueFieldHeader(
      IssueFieldNames.contractNumbers
    );
    const workTypesLabel = this.formatIssueFieldHeader(
      IssueFieldNames.workTypes
    );
    const rootCausesLabel = this.formatIssueFieldHeader(
      IssueFieldNames.rootCauses
    );
    const hazardCategoryLabel = this.formatIssueFieldHeader(
      IssueFieldNames.hazardCategory
    );
    const executedByCompaniesLabel = this.formatIssueFieldHeader(
      IssueFieldNames.executedByCompanies
    );

    const columns: GridColDef<IssueOnSingleView>[] = [
      {
        ...this.orderNumber(),
        field: GridColumnFieldIssue.orderNumber,
        align: 'center',
        filterable: false,
      },
      {
        ...this.process(this.processes),
        field: GridColumnFieldIssue.process,
        valueGetter: (_, row) => {
          const processId = row?.process?._id;

          if (!processId) {
            return [];
          }

          const process = this.processes[processId];

          if (process) {
            return [
              {
                _id: process._id,
                label: process.code ?? '',
              },
            ];
          }

          const fallbackLabel = row?.process?.label;

          return fallbackLabel
            ? [{ _id: processId, label: fallbackLabel }]
            : [];
        },
        renderCell: this.renderProcessCell,
        filterOperators: [processOperator, isEmptyOperator, isNotEmptyOperator],
      },
      this.issueNumber(),
      this.linkedInspectionNumber(),
      this.title(),
      this.workflow(),
      this.stage(),
      this.lastDecision(),
      {
        ...this.site(this.intl),
        field: GridColumnFieldIssue.sites,
        headerName: formatTranslation(this.intl, CommonTranslationKeys.site),
        valueGetter: (_, row) =>
          row?.primaryData?.site ? [row.primaryData.site] : [],
        filterOperators: [siteOperator, isEmptyOperator, isNotEmptyOperator],
      },
      {
        ...this.level(this.intl),
        field: GridColumnFieldIssue.levels,
        headerName: formatTranslation(this.intl, CommonTranslationKeys.level),
        valueGetter: (_, row) =>
          row?.primaryData?.level ? [row.primaryData.level] : [],
        filterOperators: [levelOperator, isEmptyOperator, isNotEmptyOperator],
      },
      this.targetCompletionDate(),
      this.issueCreatedAt(),
      this.modifiedAt(this.intl, this.timezone),
      this.issueCreatedBy([
        issueCreatedByOperator,
        isEmptyOperator,
        isNotEmptyOperator,
      ]),
      this.issueModifiedBy([
        modifiedByOperator,
        isEmptyOperator,
        isNotEmptyOperator,
      ]),
      this.finalCompletionDate(),
      this.detectionDate(),
      this.targetStartDate(),
      this.finalStartDate(),
      this.assignee(),
      this.executor(),
      this.createEntityListColumn(
        GridColumnFieldIssue.subcontractors,
        subcontractorsLabel,
        (row) => row?.primaryData?.subcontractors,
        { filterOperators: [subcontractorCompanyOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.contractNumbers,
        contractNumbersLabel,
        (row) => row?.primaryData?.contractNumbers,
        { filterOperators: [contractNumberOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.workTypes,
        workTypesLabel,
        (row) => row?.extendedData?.workTypes,
        { filterOperators: [workTypeOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.rootCauses,
        rootCausesLabel,
        (row) => row?.extendedData?.rootCauses,
        { filterOperators: [rootCauseOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.hazardCategory,
        hazardCategoryLabel,
        (row) => row?.extendedData?.hazardCategory,
        { filterOperators: [hazardCategoryOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.executedByCompanies,
        executedByCompaniesLabel,
        (row) => row?.extendedData?.executedByCompanies,
        { filterOperators: [executedByCompanyOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createExtendedDataLabelColumn(IssueFieldNames.priority, [priorityOperator, isEmptyOperator, isNotEmptyOperator]),
      this.createExtendedDataLabelColumn(IssueFieldNames.projectStage, [projectStageOperator, isEmptyOperator, isNotEmptyOperator]),
      this.createExtendedDataLabelColumn(IssueFieldNames.impact, [impactOperator, isEmptyOperator, isNotEmptyOperator]),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.environmentalAspect,
        [environmentalAspectOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      this.createExtendedDataLabelColumn(IssueFieldNames.effect, [effectOperator, isEmptyOperator, isNotEmptyOperator]),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.decisionToImposeFine,
        [decisionToImposeFineOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.circumstances,
        [circumstancesOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.issueVerification,
        [issueVerificationOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      this.createExtendedDataLabelColumn(IssueFieldNames.delayReason, [delayReasonOperator, isEmptyOperator, isNotEmptyOperator]),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.rejectionReason, [rejectionReasonOperator, isEmptyOperator, isNotEmptyOperator]),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.proposedCorrectiveAction,
        [correctiveActionTypeOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.employmentType,
        [employmentTypeOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      this.createExtendedDataLabelColumn(
        IssueFieldNames.spilledSubstance,
        [spilledSubstanceOperator, isEmptyOperator, isNotEmptyOperator]
      ),
      {
        field: GridColumnFieldIssue.estimatedCost,
        headerName: this.formatIssueFieldHeader(IssueFieldNames.estimatedCost),
        valueGetter: (_, row) => {
          const arr = row?.extendedData?.estimatedCost;
          if (!arr?.length) return null;
          return arr.map((c: { cost?: string }) => c?.cost).filter(Boolean).join(', ') || null;
        },
        renderCell: this.renderTextCell,
      },
      {
        field: GridColumnFieldIssue.finalCost,
        headerName: this.formatIssueFieldHeader(IssueFieldNames.finalCost),
        valueGetter: (_, row) => {
          const arr = row?.extendedData?.finalCost;
          if (!arr?.length) return null;
          return arr.map((c: { settled?: string; outstanding?: string }) => [c?.settled, c?.outstanding].filter(Boolean).join(' / ')).filter(Boolean).join(', ') || null;
        },
        renderCell: this.renderTextCell,
      },
      this.createExtendedDataNumberColumn(
        IssueFieldNames.expectedFine,
        NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS
      ),
      this.createExtendedDataNumberColumn(
        IssueFieldNames.imposedFine,
        NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS
      ),
      this.createExtendedDataNumberColumn(
        IssueFieldNames.targetAmount,
        {
          ...FILTERABLE_NUMBER_COLUMN_OPTIONS,
          ...NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS,
        }
      ),
      this.createExtendedDataNumberColumn(
        IssueFieldNames.completedAmount,
        {
          ...FILTERABLE_NUMBER_COLUMN_OPTIONS,
          ...NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS,
        }
      ),
      this.amountPercentage(),
      this.createExtendedDataNumberColumn(
        IssueFieldNames.numberOfEmployees,
        NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS
      ),
      this.createExtendedDataNumberColumn(
        IssueFieldNames.daysOfInabilityToWork,
        {
          ...FILTERABLE_NUMBER_COLUMN_OPTIONS,
          ...NUMBER_COLUMN_TEXT_LIKE_ALIGNMENT_OPTIONS,
        }
      ),
      this.createExtendedDataValueColumn(IssueFieldNames.costCode),
      this.createExtendedDataValueColumn(IssueFieldNames.personUnableToWork, {
        ...FILTERABLE_TEXT_COLUMN_OPTIONS,
      }),
      this.createExtendedDataValueColumn(IssueFieldNames.solutionProposal),
      this.createExtendedDataValueColumn(IssueFieldNames.solutionMethod),
      this.createExtendedDataValueColumn(
        IssueFieldNames.subcontractorRepresentative
      ),
      this.startDateDelay(),
      this.completionDateDelay(),
      this.responseToAuthorDelay(),
      this.targetResponseToAuthorDate(dateRangeOperator, dateIsEmptyOperator, dateIsNotEmptyOperator),
      this.createExtendedDataDateColumn(
        IssueFieldNames.finalResponseToAuthorDate,
        [dateRangeOperator, dateIsEmptyOperator, dateIsNotEmptyOperator]
      ),
      this.createExtendedDataDateColumn(
        IssueFieldNames.sickLeaveStartDate,
        [dateRangeOperator, dateIsEmptyOperator, dateIsNotEmptyOperator]
      ),
      this.createExtendedDataDateColumn(
        IssueFieldNames.sickLeaveEndDate,
        [dateRangeOperator, dateIsEmptyOperator, dateIsNotEmptyOperator]
      ),
      this.supervisor(),
      this.createExtendedDataValueColumn(IssueFieldNames.victim),
      this.createExtendedDataValueColumn(IssueFieldNames.victimJobTitle),
      this.createExtendedDataValueColumn(IssueFieldNames.employeeNumber),
      this.createExtendedDataValueColumn(IssueFieldNames.immediateMeasures),
      this.createExtendedDataValueColumn(IssueFieldNames.victimActivity),
      this.createEntityListColumn(
        GridColumnFieldIssue.injuryTypes,
        this.formatIssueFieldHeader(IssueFieldNames.injuryTypes),
        (row) => row?.extendedData?.injuryTypes,
        { filterOperators: [injuryTypesOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.injuredBodyParts,
        this.formatIssueFieldHeader(IssueFieldNames.injuredBodyParts),
        (row) => row?.extendedData?.injuredBodyParts,
        { filterOperators: [injuredBodyPartsOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createExtendedDataValueColumn(IssueFieldNames.applicationNumber),
      this.createExtendedDataValueColumn(IssueFieldNames.productType),
      this.createExtendedDataValueColumn(IssueFieldNames.producerDistributor),
      this.createExtendedDataValueColumn(IssueFieldNames.typeModel),
      this.createExtendedDataValueColumn(IssueFieldNames.installationLocation),
      this.createEntityListColumn(
        GridColumnFieldIssue.requirements,
        this.formatIssueFieldHeader(IssueFieldNames.requirements),
        (row) => row?.extendedData?.requirements,
        { filterOperators: [requirementsOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.installationBasis,
        this.formatIssueFieldHeader(IssueFieldNames.installationBasis),
        (row) => row?.extendedData?.installationBasis,
        { filterOperators: [installationBasisOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.constructionProductDocuments,
        this.formatIssueFieldHeader(IssueFieldNames.constructionProductDocuments),
        (row) => row?.extendedData?.constructionProductDocuments,
        { filterOperators: [constructionProductDocumentsOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.deviceDocuments,
        this.formatIssueFieldHeader(IssueFieldNames.deviceDocuments),
        (row) => row?.extendedData?.deviceDocuments,
        { filterOperators: [deviceDocumentsOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.createEntityListColumn(
        GridColumnFieldIssue.additionalDocuments,
        this.formatIssueFieldHeader(IssueFieldNames.additionalDocuments),
        (row) => row?.extendedData?.additionalDocuments,
        { filterOperators: [additionalDocumentsOperator, isEmptyOperator, isNotEmptyOperator] }
      ),
      this.description(),
      this.issueState(this.intl),
    ];

    return columns.map((col) => {
      const { flex, ...columnWithoutFlex } = col;

      return {
        ...columnWithoutFlex,
        // Only columns with their own operators: `undefined` would hide the defaults of the column type.
        ...(col.filterOperators ? { filterOperators: withNegatedFilterOperators(col.filterOperators, this.intl) } : {}),
        sortable: SORTABLE_ISSUE_FIELDS.has(col.field as string),
      };
    });
  }
}
